// ─── LexaFAB ─────────────────────────────────────────────────────────────
// Bouton flottant or en bas à droite + drawer chat avec Lexa (assistante IA Lexback).
// 3 cas d'usage couverts :
//   1. Poser une question juridique (Q&A général) → stay in chat
//   2. Demander statut d'un dossier en cours → stay in chat
//   3. Vouloir déposer un nouveau dossier → Lexa redirige vers /lexa (page complète)
//
// La conversation parle à POST /api/lexa/chat avec body { message, conversation_id? }
// Le backend gère la persistance + tools (Pappers, lookup dossier, etc.).
//
// Le composant gère son propre state (open, messages, input). Aucun coupling avec Layout.
// Couleurs : palette espresso Lexback (or #d9a847, sombre #1f1612).

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

const C = {
  gold: 'var(--brand-primary, #d9a847)',
  goldLight: 'var(--brand-primary, #e8c170)',
  espresso: '#1f1612',
  bg: '#fefcf7',
  bgChat: '#f9f6ef',
  text: '#1f1612',
  text2: '#5a534a',
  text3: '#9a9088',
  border: 'rgba(0,0,0,0.08)',
};

// ─── Mini-renderer Markdown ──────────────────────────────────────────────
// Couvre les éléments que Claude renvoie typiquement : gras **x**, italique *x*,
// titres ### x, listes - x / * x, séparateurs ---, sauts de ligne.
// Retourne un tableau de React nodes prêts à rendre dans une <div>.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const nodes = [];
  let listBuffer = [];

  // Inline formatting (gras + italique) sur une ligne
  const inline = (s) => {
    const parts = [];
    let rest = s;
    let key = 0;
    // Boucle simple : on cherche le 1er marqueur, on split, on continue
    while (rest.length > 0) {
      // Gras (**)
      const boldMatch = rest.match(/\*\*([^*]+)\*\*/);
      // Italique (*) — sans confusion avec **
      const italMatch = rest.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
      const firstBold = boldMatch ? rest.indexOf(boldMatch[0]) : -1;
      const firstItal = italMatch ? rest.indexOf(italMatch[0]) : -1;

      if (firstBold === -1 && firstItal === -1) {
        parts.push(rest);
        break;
      }
      const useBold = firstBold !== -1 && (firstItal === -1 || firstBold < firstItal);
      const match = useBold ? boldMatch : italMatch;
      const idx = useBold ? firstBold : firstItal;
      if (idx > 0) parts.push(rest.slice(0, idx));
      parts.push(
        useBold
          ? <strong key={`b${key++}`} style={{ fontWeight: 600 }}>{match[1]}</strong>
          : <em key={`i${key++}`}>{match[1]}</em>
      );
      rest = rest.slice(idx + match[0].length);
    }
    return parts;
  };

  const flushList = () => {
    if (listBuffer.length > 0) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} style={{ margin: '4px 0 4px 0', paddingLeft: '18px' }}>
          {listBuffer.map((item, i) => (
            <li key={i} style={{ marginBottom: '2px', lineHeight: 1.5 }}>{inline(item)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    // Séparateur ---
    if (/^---+$/.test(trimmed)) {
      flushList();
      nodes.push(<hr key={`hr-${i}`} style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '8px 0' }} />);
      return;
    }
    // Titre niveau 3 (###)
    if (/^###\s+/.test(trimmed)) {
      flushList();
      nodes.push(
        <div key={`h-${i}`} style={{ fontWeight: 600, fontSize: '13px', marginTop: '6px', marginBottom: '3px' }}>
          {inline(trimmed.replace(/^###\s+/, ''))}
        </div>
      );
      return;
    }
    // Liste (- ou *)
    if (/^[-*]\s+/.test(trimmed)) {
      listBuffer.push(trimmed.replace(/^[-*]\s+/, ''));
      return;
    }
    // Ligne vide → flush list + saut visuel
    if (trimmed === '') {
      flushList();
      nodes.push(<div key={`sp-${i}`} style={{ height: '4px' }} />);
      return;
    }
    // Paragraphe normal
    flushList();
    nodes.push(<div key={`p-${i}`} style={{ marginBottom: '2px' }}>{inline(trimmed)}</div>);
  });
  flushList();
  return nodes;
}

export default function LexaFAB() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [fabHover, setFabHover] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Bonjour ! Je suis Lexa, votre assistante juridique. Je peux répondre à vos questions, vous renseigner sur un dossier en cours, ou vous aider à en déposer un nouveau. Comment puis-je vous aider ?",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // ESC ferme + scroll lock body quand drawer ouvert
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Auto-scroll vers le bas à chaque nouveau message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input quand drawer s'ouvre
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;

    // 1. Ajoute message user localement
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const token = localStorage.getItem('lexback_token');
      const res = await fetch(`${API_URL}/api/lexa/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();

      // Save conversation_id si premier message
      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }

      // 2. Affiche réponse de Lexa
      // Le backend renvoie { assistant_message, conversation_id, title, tool_calls }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.assistant_message || data.response || data.message || data.content || 'Désolée, je n\'ai pas pu traiter votre demande.',
        // Action proposée par Lexa (si elle veut rediriger vers une page)
        action: data.action || null,
      }]);
    } catch (err) {
      console.error('Lexa chat failed:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Je rencontre un problème technique. Vous pouvez réessayer, ou ouvrir la page complète si vous souhaitez déposer un dossier.",
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Suggestions rapides (chips)
  const quickActions = [
    { label: 'Déposer un nouveau dossier', action: () => { setOpen(false); navigate('/dossiers/nouveau'); } },
    { label: 'État de mes dossiers', action: () => { setInput('Quel est l\'état de mes dossiers en cours ?'); inputRef.current?.focus(); } },
    { label: 'Question juridique', action: () => { setInput(''); inputRef.current?.focus(); } },
  ];

  return (
    <>
      {/* ─── Bouton flottant or ──────────────────────────────────────── */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          onMouseEnter={() => setFabHover(true)}
          onMouseLeave={() => setFabHover(false)}
          aria-label="Ouvrir le chat avec Lexa"
          title="Discuter avec Lexa"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1000,
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: fabHover
              ? '0 12px 28px rgba(217,168,71,0.45), 0 3px 8px rgba(0,0,0,0.1)'
              : '0 6px 18px rgba(217,168,71,0.3), 0 2px 6px rgba(0,0,0,0.08)',
            transform: fabHover ? 'scale(1.06)' : 'scale(1)',
            transition: 'transform 0.18s, box-shadow 0.18s',
          }}
        >
          {/* Icône étoile (sparkle) */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" />
            <path d="M19 4 L19.7 6 L21.5 6.5 L19.7 7 L19 9 L18.3 7 L16.5 6.5 L18.3 6 Z" />
          </svg>
          {/* Label "Lexa" au hover */}
          <span style={{
            position: 'absolute',
            right: '64px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: C.espresso,
            color: '#fff',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            opacity: fabHover ? 1 : 0,
            pointerEvents: 'none',
            transition: 'opacity 0.18s',
          }}>
            Lexa · Assistante IA
          </span>
        </button>
      )}

      {/* ─── Drawer chat ────────────────────────────────────────────── */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.25)', zIndex: 999,
              backdropFilter: 'blur(2px)',
            }}
          />

          {/* Drawer panneau droit */}
          <aside
            role="dialog"
            aria-label="Chat avec Lexa"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              zIndex: 1000,
              width: '420px',
              height: 'min(620px, calc(100vh - 48px))',
              background: C.bg,
              borderRadius: '16px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.18), 0 8px 16px rgba(0,0,0,0.08)',
              border: `1px solid ${C.border}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'lexaSlideIn 0.22s ease-out',
            }}
          >
            <style>{`
              @keyframes lexaSlideIn {
                from { opacity: 0; transform: translateY(12px) scale(0.98); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
              @keyframes lexaTypingDot {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-3px); }
              }
            `}</style>

            {/* Header */}
            <div style={{
              padding: '14px 16px',
              borderBottom: `1px solid ${C.border}`,
              background: `linear-gradient(135deg, ${C.espresso} 0%, #2a1f1a 100%)`,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              {/* Avatar étoile */}
              <div style={{
                width: '32px', height: '32px',
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>Lexa</div>
                <div style={{ fontSize: '10.5px', color: '#fff', opacity: 0.6, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80' }} />
                  Assistante IA · Lexback
                </div>
              </div>
              {/* Bouton page complète */}
              <button
                onClick={() => { setOpen(false); navigate('/lexa'); }}
                title="Ouvrir Lexa en plein écran"
                style={{
                  background: 'transparent', border: 'none',
                  color: '#fff', opacity: 0.6, cursor: 'pointer',
                  padding: '4px', borderRadius: '4px',
                  display: 'flex', alignItems: 'center',
                }}
                onMouseEnter={e => e.target.style.opacity = 1}
                onMouseLeave={e => e.target.style.opacity = 0.6}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
              {/* Bouton fermer */}
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                style={{
                  background: 'transparent', border: 'none',
                  color: '#fff', opacity: 0.6, cursor: 'pointer',
                  fontSize: '20px', lineHeight: 1, padding: '0 4px',
                }}
                onMouseEnter={e => e.target.style.opacity = 1}
                onMouseLeave={e => e.target.style.opacity = 0.6}
              >×</button>
            </div>

            {/* Zone messages */}
            <div
              ref={scrollRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '14px',
                background: C.bgChat,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {messages.map((m, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '82%',
                    padding: '9px 13px',
                    borderRadius: m.role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                    background: m.role === 'user' ? C.espresso : '#fff',
                    color: m.role === 'user' ? '#fff' : (m.error ? '#b3261e' : C.text),
                    fontSize: '12.5px',
                    lineHeight: 1.5,
                    border: m.role === 'assistant' ? `1px solid ${C.border}` : 'none',
                    whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
                    wordBreak: 'break-word',
                  }}>
                    {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
                    {/* Si Lexa propose une action (ex. ouvrir une page) */}
                    {m.action && (
                      <div style={{ marginTop: '8px' }}>
                        <button
                          onClick={() => {
                            if (m.action.type === 'navigate' && m.action.to) {
                              setOpen(false);
                              navigate(m.action.to);
                            }
                          }}
                          style={{
                            background: C.gold, color: '#fff', border: 'none',
                            padding: '5px 12px', borderRadius: '6px',
                            fontSize: '11.5px', fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          {m.action.label || 'Ouvrir'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Indicateur "Lexa écrit…" */}
              {sending && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    padding: '11px 14px',
                    borderRadius: '14px 14px 14px 3px',
                    background: '#fff',
                    border: `1px solid ${C.border}`,
                    display: 'flex', gap: '4px', alignItems: 'center',
                  }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{
                        width: '6px', height: '6px',
                        borderRadius: '50%',
                        background: C.gold,
                        animation: `lexaTypingDot 1.2s infinite`,
                        animationDelay: `${i * 0.15}s`,
                      }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Quick actions chips (seulement au tout début) */}
              {messages.length === 1 && !sending && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '6px',
                  marginTop: '8px', paddingTop: '8px',
                  borderTop: `1px dashed ${C.border}`,
                }}>
                  <div style={{ fontSize: '10px', color: C.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                    Suggestions
                  </div>
                  {quickActions.map((qa, i) => (
                    <button
                      key={i}
                      onClick={qa.action}
                      style={{
                        background: '#fff',
                        border: `1px solid ${C.border}`,
                        color: C.text,
                        padding: '7px 11px',
                        borderRadius: '8px',
                        fontSize: '11.5px',
                        fontWeight: 400,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = C.gold;
                        e.currentTarget.style.background = '#fefaf0';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = C.border;
                        e.currentTarget.style.background = '#fff';
                      }}
                    >
                      → {qa.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input bar */}
            <div style={{
              padding: '10px 12px',
              borderTop: `1px solid ${C.border}`,
              background: '#fff',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
            }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Posez votre question…"
                rows={1}
                disabled={sending}
                style={{
                  flex: 1,
                  background: C.bgChat,
                  border: `1px solid ${C.border}`,
                  borderRadius: '10px',
                  padding: '8px 11px',
                  fontSize: '12.5px',
                  fontFamily: 'inherit',
                  color: C.text,
                  outline: 'none',
                  resize: 'none',
                  maxHeight: '100px',
                  lineHeight: 1.4,
                }}
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                style={{
                  background: !input.trim() || sending ? 'rgba(217,168,71,0.3)' : C.gold,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  width: '38px', height: '38px',
                  cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.12s',
                }}
                aria-label="Envoyer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
